import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

const CONTACT_EMAIL = "mohammed@alghafly.com";
const LAST_UPDATED = "February 7, 2026";

// One shared layout for all four legal pages so they always look and read
// the same. Each page passes its title, plain-English summary and formal
// content as props. Kids and grown-ups both get a friendly recap first,
// then a full formal block for anyone who actually needs it.
const LegalLayout = ({ title, testId, subtitle, plain, formal }) => {
  usePageMeta(`${title} · 67Cafe`, subtitle);
  return (
    <div
      className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-8"
      data-testid={testId}
    >
      <Link
        to="/shop"
        className="btn-3d btn-sm btn-ghost mb-4"
        data-testid={`${testId}-back`}
      >
        <ArrowLeft size={18} strokeWidth={3} /> Back
      </Link>

      <header className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl leading-tight" data-testid={`${testId}-title`}>
          {title}
        </h1>
        <p className="text-sm mt-2" style={{ color: "#484848" }}>
          Last updated {LAST_UPDATED}
        </p>
      </header>

      <section
        className="chunky-card p-5 sm:p-6 mb-6"
        style={{ background: "#EAF6FF" }}
        data-testid={`${testId}-plain`}
      >
        <h2 className="font-display text-xl mb-3">The friendly version</h2>
        <div className="space-y-3 text-base leading-relaxed" style={{ color: "#484848" }}>
          {plain}
        </div>
      </section>

      <section
        className="chunky-card p-5 sm:p-6 mb-6"
        style={{ background: "#fff" }}
        data-testid={`${testId}-formal`}
      >
        <h2 className="font-display text-xl mb-3">The formal version</h2>
        <div className="space-y-4 text-sm leading-relaxed" style={{ color: "#484848" }}>
          {formal}
        </div>
      </section>

      <p className="text-sm" style={{ color: "#484848" }}>
        Questions? Email <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="underline"
          data-testid={`${testId}-contact`}
          style={{ color: "#FF5A5F" }}
        >{CONTACT_EMAIL}</a>.
      </p>
    </div>
  );
};

export const Privacy = () => (
  <LegalLayout
    title="Privacy Policy"
    testId="legal-privacy"
    subtitle="How 67Cafe collects and uses information."
    plain={
      <>
        <p>67Cafe is a pretend shop. Everything you spend or earn is fake coins — we never take real money and we never ask for card details.</p>
        <p>We remember your nickname, avatar, coin totals, stickers and orders on your own device so the game feels like yours. If you tell us your birthday, we save it so we can wish you happy birthday and understand who plays.</p>
        <p>If you sign in with Google, we only see your name, email and profile picture. We do not read your Gmail, your contacts, or any other Google service.</p>
        <p>We also collect basic visit stats — country, city, device type, timezone, language, and how many minutes you played — so we can make 67Cafe better and spot broken things.</p>
        <p>We never sell your data. Ever.</p>
      </>
    }
    formal={
      <>
        <p><strong>1. Data Controller.</strong> This Site (&quot;67Cafe&quot;, &quot;we&quot;, &quot;us&quot;) is operated by the owner of the domain 67cafe.com. For privacy inquiries contact <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "#FF5A5F" }}>{CONTACT_EMAIL}</a>.</p>
        <p><strong>2. Data We Collect.</strong> (a) <em>Client-side game state</em> stored in your browser&#39;s localStorage: nickname, avatar, date of birth (optional), wallet coins, cart, wishlist, orders, streak, stickers, achievements. (b) <em>Server-side analytics</em> stored in our MongoDB database: an anonymous visitor identifier, first/last seen timestamps, session playtime seconds, page paths, referrer, user agent, timezone, language, viewport, device type, IP-derived country/region/city (via ipapi.co), and per-game aggregate stats. (c) <em>Google Sign-In (optional)</em>: your Google email, display name and profile picture, delivered by Emergent Authentication. We do not receive, store or request any other Google scope.</p>
        <p><strong>3. Cookies &amp; Similar Technologies.</strong> We set a strictly-necessary admin session cookie for the private admin dashboard. Regular visitors are identified by an anonymous browser-local identifier — not a tracking cookie.</p>
        <p><strong>4. Lawful Basis.</strong> Legitimate interest in operating and improving a playful all-ages game, with your consent captured when you submit optional information such as date of birth or when you sign in with Google.</p>
        <p><strong>5. Data Retention.</strong> Client-side game state persists until you clear your browser. Server-side analytics are retained for up to 24 months and then anonymised. Google Sign-In records are retained while your account exists on 67Cafe.</p>
        <p><strong>6. Sharing.</strong> We do not sell personal data. We share data only with (a) the geolocation lookup vendor ipapi.co, (b) our authentication provider Emergent Authentication for Google Sign-In, and (c) hosting infrastructure. All parties process data on our behalf.</p>
        <p><strong>7. Children.</strong> 67Cafe is a playful pretend hangout suitable for all ages. If you are a child in a jurisdiction that requires parental consent for online services, please ask an adult before entering a nickname or birthday.</p>
        <p><strong>8. Your Rights.</strong> You may request access, correction, deletion, restriction, or portability of your data at any time by emailing <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "#FF5A5F" }}>{CONTACT_EMAIL}</a>. You may also clear all client-side data instantly using your browser&#39;s clear-site-data settings.</p>
        <p><strong>9. Security.</strong> We use industry-standard TLS in transit and secure managed hosting. No system is perfectly secure; you use the Site at your own risk.</p>
        <p><strong>10. Changes.</strong> We may update this policy. Continued use of the Site after changes constitutes acceptance.</p>
      </>
    }
  />
);

export const Terms = () => (
  <LegalLayout
    title="Terms of Use"
    testId="legal-terms"
    subtitle="The rules for playing at 67Cafe."
    plain={
      <>
        <p>67Cafe is a free, ad-free, pretend hangout. All coins, shop items and orders are imaginary. Nothing you &quot;buy&quot; here has any real-world value.</p>
        <p>Play nicely. Do not try to cheat, hack, spam, or share hurtful things. If you break the game or the community, we can turn off your access.</p>
        <p>The characters, art, code and content of 67Cafe belong to us. You can enjoy the Site as much as you like — you just cannot copy it wholesale for your own products.</p>
        <p>We do our best but sometimes things break. We are not responsible for lost pretend coins, missed streaks or a spilled cup of imaginary coffee.</p>
      </>
    }
    formal={
      <>
        <p><strong>1. Acceptance.</strong> By accessing or using 67Cafe you agree to be bound by these Terms. If you do not agree, do not use the Site.</p>
        <p><strong>2. Eligibility.</strong> 67Cafe is a general-audience entertainment product. Minors should use the Site with the involvement of a parent or guardian where local law requires.</p>
        <p><strong>3. Virtual Items.</strong> All coins, wallets, orders, stickers, achievements and other in-game assets are fictional, non-transferable, non-redeemable and hold no monetary value. They may be revoked, altered or reset at any time without notice or compensation.</p>
        <p><strong>4. No Payments.</strong> 67Cafe does not process real payments and does not sell real goods. Any appearance of pricing is purely part of the pretend-shop simulation.</p>
        <p><strong>5. Acceptable Use.</strong> You agree not to: (a) reverse-engineer, decompile, scrape or automate the Site; (b) attempt to gain unauthorised access to admin dashboards, analytics, or other accounts; (c) upload harmful, unlawful or infringing content; (d) impersonate any person or misrepresent your identity; (e) interfere with other users&#39; play.</p>
        <p><strong>6. Accounts &amp; Sign-In.</strong> If you sign in with Google, you are responsible for keeping your Google credentials safe. We may suspend or terminate any account that appears to be misused. The private admin panel is restricted to authorised personnel; unauthorised access is logged and prohibited.</p>
        <p><strong>7. Intellectual Property.</strong> All content on 67Cafe — including the name &quot;67Cafe&quot;, the coffee-cup mark, the pretend-shop artwork, the games, the source code, and the copy — is owned by us and protected by copyright and trademark laws. Except for the limited licence to use the Site as intended, no rights are granted.</p>
        <p><strong>8. User-Submitted Content.</strong> If you submit a nickname, avatar or other content, you grant us a worldwide, royalty-free, non-exclusive licence to use that content solely to operate and improve the Site.</p>
        <p><strong>9. Disclaimers.</strong> The Site is provided &quot;as is&quot; and &quot;as available&quot;, without warranties of any kind. We do not guarantee uninterrupted, secure or error-free operation.</p>
        <p><strong>10. Limitation of Liability.</strong> To the maximum extent permitted by law, our aggregate liability for any claim arising out of or relating to the Site is limited to USD 0, because the Site is free and its virtual assets are not real.</p>
        <p><strong>11. Termination.</strong> We may suspend or terminate your access at any time for any reason, including violation of these Terms.</p>
        <p><strong>12. Governing Law.</strong> These Terms are governed by the laws of the jurisdiction in which the Site operator resides, without regard to its conflict-of-laws rules. Disputes will be resolved in the competent courts of that jurisdiction.</p>
        <p><strong>13. Changes.</strong> We may update these Terms. Continued use after changes constitutes acceptance. Contact: <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "#FF5A5F" }}>{CONTACT_EMAIL}</a>.</p>
      </>
    }
  />
);

export const Dmca = () => (
  <LegalLayout
    title="DMCA Policy"
    testId="legal-dmca"
    subtitle="How to report copyrighted content on 67Cafe."
    plain={
      <>
        <p>If you think something on 67Cafe copies your work without permission, please tell us. We take copyright seriously and will act quickly.</p>
        <p>Email <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "#FF5A5F" }}>{CONTACT_EMAIL}</a> with the details below and we will look into it right away.</p>
      </>
    }
    formal={
      <>
        <p>In accordance with the Digital Millennium Copyright Act of 1998, 17 U.S.C. § 512 (&quot;DMCA&quot;), 67Cafe responds to notices of alleged copyright infringement.</p>
        <p><strong>1. How to File a Notice.</strong> Send a written communication to <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "#FF5A5F" }}>{CONTACT_EMAIL}</a> including all of the following:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>A physical or electronic signature of the person authorised to act on behalf of the copyright owner.</li>
          <li>Identification of the copyrighted work claimed to have been infringed.</li>
          <li>Identification of the material that is claimed to be infringing, and a URL or other information sufficient to locate the material on the Site.</li>
          <li>Your address, telephone number and email address.</li>
          <li>A statement, made under penalty of perjury, that you have a good-faith belief that the disputed use is not authorised by the copyright owner, its agent, or the law.</li>
          <li>A statement, made under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or authorised to act on its behalf.</li>
        </ul>
        <p><strong>2. Counter-Notice.</strong> If your material was removed and you believe it was removed in error, you may submit a counter-notice containing the corresponding information above plus a statement consenting to the jurisdiction of the appropriate federal court.</p>
        <p><strong>3. Repeat Infringers.</strong> 67Cafe will terminate the accounts of visitors who are found to be repeat infringers.</p>
        <p><strong>4. False Claims.</strong> Under 17 U.S.C. § 512(f), any person who knowingly materially misrepresents that material is infringing may be liable for damages, including costs and attorneys&#39; fees.</p>
        <p><strong>Designated Agent.</strong> Copyright Agent, 67Cafe — email <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "#FF5A5F" }}>{CONTACT_EMAIL}</a>.</p>
      </>
    }
  />
);

export const LegalNotices = () => (
  <LegalLayout
    title="Legal Notices"
    testId="legal-notices"
    subtitle="Trademarks, third-party libraries and everything else."
    plain={
      <>
        <p>67Cafe is a pretend shop. Real payments? Nope. Real coffee? Sadly no.</p>
        <p>The 67Cafe name and coffee cup are ours. Everything you see is either handmade for the Site or clearly credited below.</p>
        <p>67Cafe is not affiliated with Google, Emergent, or any other brand mentioned on this page.</p>
      </>
    }
    formal={
      <>
        <p><strong>1. Trademarks.</strong> &quot;67Cafe&quot;, the coffee-cup mark, and associated logos are trademarks of the Site operator. All other product, service and company names appearing on the Site are the trademarks of their respective owners and are used for identification purposes only.</p>
        <p><strong>2. Third-Party Services.</strong> The Site uses the following third-party services: Emergent Authentication (Google Sign-In delivery), ipapi.co (IP geolocation), and Google web fonts. Each service is bound by its own terms and privacy policy.</p>
        <p><strong>3. Open-Source Notice.</strong> The Site is built with, among other libraries, React, React Router, TailwindCSS, shadcn/ui, lucide-react, FastAPI, Motor (async MongoDB driver), and httpx. Each library is licensed under its own permissive open-source licence and copyright resides with its respective authors. Full attributions available on request.</p>
        <p><strong>4. Disclaimer of Endorsement.</strong> References to third-party brands, products or services on the Site do not imply endorsement by their owners.</p>
        <p><strong>5. Export &amp; Sanctions.</strong> You may not access or use the Site if doing so would violate any applicable export-control or economic-sanctions law.</p>
        <p><strong>6. Consumer Rights.</strong> Nothing in the Site or these notices is intended to exclude or limit consumer rights that cannot be waived under applicable law.</p>
        <p><strong>7. Severability.</strong> If any provision of the Site&#39;s legal terms is found to be unenforceable, the remaining provisions shall remain in full force and effect.</p>
        <p><strong>8. Contact.</strong> For any legal matter, please email <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "#FF5A5F" }}>{CONTACT_EMAIL}</a>.</p>
      </>
    }
  />
);
